import React, { useCallback,useState,useRef,useEffect } from 'react';
import './Register.css';
import LoadingIndicator from './LoadingIndicator';

function FancyDropdown({
  label,
  value,
  options,
  placeholder = 'Select',
  loading = false,
  disabled = false,
  error = '',
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = options.find((item) => String(item.value) === String(value));

  return (
    <div className="fd-field" ref={wrapperRef}>
      <label className="fd-label">{label}</label>

      <button
        type="button"
        className={`fd-trigger ${open ? 'open' : ''}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
      >
        <span className={`fd-value ${!selected ? 'placeholder' : ''}`}>
          {loading ? (
            <LoadingIndicator variant="tiny" label={`Loading ${label.toLowerCase()}...`} />
          ) : (
            selected?.label || placeholder
          )}
        </span>
        <span className="fd-caret" aria-hidden="true" />
      </button>

      {error ? <div className="fd-error">{error}</div> : null}

      {open ? (
        <div className="fd-menu">
          {options.map((item) => (
            <button
              key={item.value}
              type="button"
              className={`fd-option ${String(value) === String(item.value) ? 'selected' : ''}`}
              onClick={() => {
                onChange(item.value);
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}


export default function Register({ onShowLogin }) {
  const [form, setForm] = useState({
    username: '',
    emp_name: '',
    password: '',
    mail_id: '',
    phone: '',
    designation: '',
    role_id: '',
  });
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('');
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  function onChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
    setMsg('');
  }

 const loadRoles = useCallback(async () => {
  setRolesLoading(true);
  setRolesError('');

  try {
    const res = await fetch('/api/users/roles', { credentials: 'include' });
    const data = await res.json().catch(() => ({}));

    if (res.status === 401) {
      return;
    }

    if (!res.ok) {
      throw new Error(data.message || 'Unable to load roles');
    }

    setRoles(Array.isArray(data) ? data : []);
  } catch (err) {
    setRoles([]);
    setRolesError(err.message || 'Unable to load roles');
  } finally {
    setRolesLoading(false);
  }
}, []); // ✅ Add empty dependency array

  // Add this useEffect to load roles on mount
  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  async function onSubmit(e) {
    e.preventDefault();
    setMsg('');
    setMsgType('');

    if (!form.username.trim() || !form.emp_name.trim() || !form.mail_id.trim() || !form.password) {
      setMsg('All fields are required');
      setMsgType('error');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/users/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username.trim(),
          emp_name: form.emp_name.trim(),
          mail_id: form.mail_id.trim(),
          phone: form.phone,
          password: form.password,
          role_id: form.role_id,
          designation: form.designation,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMsg(data.error || 'Registration failed');
        setMsgType('error');
        return;
      }
      setMsg(`Registered ${data.username} successfully`);
      setMsgType('success');
      setForm({ username: '', emp_name: '', password: '', mail_id: '', phone: '', role_id: '', designation: '' });
    } catch {
      setMsg('Network error');
      setMsgType('error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="register-container">
      <div className="register-card">
        <h1>Create Account</h1>
        <form onSubmit={onSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="register-username">Username</label>
            <input
              id="register-username"
              name="username"
              value={form.username}
              onChange={onChange}
              placeholder="Enter username"
              autoComplete="username"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="register-emp-name">Employee Name</label>
            <input
              id="register-emp-name"
              name="emp_name"
              value={form.emp_name}
              onChange={onChange}
              placeholder="Enter full name"
              autoComplete="name"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="register-email">Email</label>
            <input
              id="register-email"
              name="mail_id"
              type="email"
              value={form.mail_id}
              onChange={onChange}
              placeholder="Enter email address"
              autoComplete="email"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="register-designation">Designation</label>
            <input
              id="register-designation"
              name="designation"
              value={form.designation}
              onChange={onChange}
              placeholder="Enter designation"
              autoComplete="organization-title"
              disabled={loading}
              required
            />
          </div>


          <div className="form-group">
            <label htmlFor="register-phone">Phone Number</label>
            <input
              id="register-phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={onChange}
              placeholder="Enter phone number"
              autoComplete="tel"
              disabled={loading}
              maxLength="10"
              required
            />
          </div>

         <FancyDropdown
                label="Role"
                value={form.role_id}
                onChange={(val) => setForm((prev) => ({ ...prev, role_id: val }))}
                options={roles.map((role) => ({
                  value: role.id,
                  label: role.role_name,
                }))}
                placeholder="Select role"
                loading={rolesLoading}
                error={rolesError}
              />
             

          <div className="form-group password-field">
  <label htmlFor="register-password">Password</label>
  <div className="password-input-wrapper">
    <input
      id="register-password"
      name="password"
      type={showPassword ? 'text' : 'password'}
      value={form.password}
      onChange={onChange}
      placeholder="Enter password"
      autoComplete="new-password"
      disabled={loading}
      required
    />
    <button
      type="button"
      className="toggle-password-btn"
      onClick={() => setShowPassword(!showPassword)}
      disabled={loading}
      aria-label={showPassword ? 'Hide password' : 'Show password'}
    >
      {showPassword ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
          <line x1="1" y1="1" x2="23" y2="23"></line>
        </svg>
      )}
    </button>
  </div>
</div>

          <button type="submit" className="register-button" disabled={loading}>
            {loading ? <LoadingIndicator variant="button" label="Registering..." /> : 'Register'}
          </button>

          {msg && <div className={`message ${msgType}`}>{msg}</div>}
        </form>

    <p className="register-switch">
          <button type="button" onClick={onShowLogin} style={{ fontSize: '14px', textDecoration: 'none' }}>
            ← Back to Tasks
          </button>
        </p>
      </div>
    </div>
  );
}